import type { FormField } from './types';

const BASE_INPUT =
  'w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900';

interface FieldEditorProps {
  field: FormField;
  onChange: (updated: FormField) => void;
}

export function FieldEditor({ field, onChange }: FieldEditorProps) {
  const update = (patch: Partial<FormField>) => onChange({ ...field, ...patch });

  return (
    <div className="space-y-4 p-4">
      <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
        Field Properties
      </h3>

      {/* Label */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700" htmlFor="fe-label">
          Label <span className="text-red-500">*</span>
        </label>
        <input
          id="fe-label"
          type="text"
          className={BASE_INPUT}
          value={field.label}
          onChange={(e) => update({ label: e.target.value })}
        />
      </div>

      {/* Type (read-only display) */}
      <div className="space-y-1">
        <span className="block text-xs font-medium text-slate-700">Type</span>
        <span className="inline-block rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
          {field.type}
        </span>
      </div>

      {/* Required */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={field.required}
          onChange={(e) => update({ required: e.target.checked })}
          className="rounded border-slate-300 text-slate-900 focus:ring-slate-900"
        />
        <span className="text-sm text-slate-700">Required</span>
      </label>

      {/* Placeholder — text only */}
      {(field.type === 'text' || field.type === 'number') && (
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700" htmlFor="fe-placeholder">
            Placeholder
          </label>
          <input
            id="fe-placeholder"
            type="text"
            className={BASE_INPUT}
            value={field.placeholder ?? ''}
            onChange={(e) => update({ placeholder: e.target.value || undefined })}
          />
        </div>
      )}

      {/* Help text */}
      <div className="space-y-1">
        <label className="block text-xs font-medium text-slate-700" htmlFor="fe-help">
          Help text
        </label>
        <input
          id="fe-help"
          type="text"
          className={BASE_INPUT}
          value={field.helpText ?? ''}
          onChange={(e) => update({ helpText: e.target.value || undefined })}
          placeholder="Shown below the label"
        />
      </div>

      {/* Number: min / max */}
      {field.type === 'number' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700" htmlFor="fe-min">
              Min
            </label>
            <input
              id="fe-min"
              type="number"
              className={BASE_INPUT}
              value={field.min ?? ''}
              onChange={(e) =>
                update({ min: e.target.value !== '' ? Number(e.target.value) : undefined })
              }
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700" htmlFor="fe-max">
              Max
            </label>
            <input
              id="fe-max"
              type="number"
              className={BASE_INPUT}
              value={field.max ?? ''}
              onChange={(e) =>
                update({ max: e.target.value !== '' ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </div>
      )}

      {/* Select / multiselect: options */}
      {(field.type === 'select' || field.type === 'multiselect') && (
        <div className="space-y-1">
          <label className="block text-xs font-medium text-slate-700" htmlFor="fe-options">
            Options <span className="text-slate-400 font-normal">(one per line)</span>
          </label>
          <textarea
            id="fe-options"
            className={`${BASE_INPUT} h-28 resize-none`}
            value={(field.options ?? []).join('\n')}
            onChange={(e) =>
              update({
                options: e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder={'Option A\nOption B\nOption C'}
          />
          <p className="text-xs text-slate-400">
            {field.options?.length ?? 0} option{field.options?.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* File: accept / maxSizeMb */}
      {field.type === 'file' && (
        <>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700" htmlFor="fe-accept">
              Accepted file types
            </label>
            <input
              id="fe-accept"
              type="text"
              className={BASE_INPUT}
              value={field.accept ?? ''}
              onChange={(e) => update({ accept: e.target.value || undefined })}
              placeholder=".pdf,.docx,.xlsx"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-slate-700" htmlFor="fe-maxsize">
              Max file size (MB)
            </label>
            <input
              id="fe-maxsize"
              type="number"
              className={BASE_INPUT}
              value={field.maxSizeMb ?? ''}
              min={1}
              onChange={(e) =>
                update({ maxSizeMb: e.target.value !== '' ? Number(e.target.value) : undefined })
              }
            />
          </div>
        </>
      )}
    </div>
  );
}
